import { EventRepository, EventListOptions, EventListResponse, EventCountOptions } from '../../domain/repositories/EventRepository';
import { Event, EventStatus, SensorData } from '../../domain/entities/Event';
import { ILoggerService } from '../../domain/services/LoggerService';
import { UserContext, isLocationScopedContext } from '../../domain/value-objects/UserContext';
import { BaseFirebaseRepository } from './BaseFirebaseRepository';
import { FilterCondition, DateRangeFilter } from '../services/FirestoreQueryBuilder';

export class FirebaseEventRepository extends BaseFirebaseRepository<Event> implements EventRepository {
  constructor(logger: ILoggerService) {
    super('events', logger);
  }

  protected convertFirestoreToEntity(id: string, data: any): Event {
    return new Event(
      id,
      data.alertId || '',
      data.alertName || '',
      data.sensorId || '',
      data.sensorName || '',
      data.locationId || '',
      data.locationName || '',
      data.organizationId || '',
      this.convertFirestoreDate(data.createdAt) || new Date(),
      this.convertFirestoreDateNullable(data.notifiedAt),
      this.convertFirestoreDateNullable(data.triggeredAt) || new Date(),
      data.confirmed ?? false,
      this.convertFirestoreDateNullable(data.resolvedAt),
      Array.isArray(data.confirmedBy) ? data.confirmedBy : [],
      data.repeatCountdown ?? null,
      this.validateEventStatus(data.status),
      this.convertSensorData(data.data || {})
    );
  }

  protected getDefaultSortField(): string {
    return 'triggeredAt';
  }

  protected mapSortField(sortBy: string): string {
    switch (sortBy) {
      case 'alertName':
        return 'alertName';
      case 'status':
        return 'status';
      case 'triggeredAt':
      default:
        return 'triggeredAt';
    }
  }

  protected applySearchFilter(events: Event[], search: string): Event[] {
    const searchLower = search.toLowerCase();
    return events.filter(event =>
      event.alertName.toLowerCase().includes(searchLower) ||
      event.sensorName.toLowerCase().includes(searchLower) ||
      event.locationName.toLowerCase().includes(searchLower)
    );
  }



  async findAll(options: EventListOptions = {}, userContext?: UserContext): Promise<EventListResponse> {
    const {
      organizationId,
      alertId,
      sensorId,
      locationId,
      status,
      dateFrom,
      dateTo,
      confirmed,
      ...queryOptions
    } = options;

    // Build additional filters
    const additionalFilters: FilterCondition[] = [];
    
    // Add location filter if provided
    if (locationId) {
      additionalFilters.push({ field: 'locationId', operator: '==', value: locationId });
    }
    
    if (alertId) {
      additionalFilters.push({ field: 'alertId', operator: '==', value: alertId });
    }
    
    if (sensorId) {
      additionalFilters.push({ field: 'sensorId', operator: '==', value: sensorId });
    }
    
    if (status) {
      if (Array.isArray(status) && status.length > 1) {
        additionalFilters.push({ field: 'status', operator: 'in', value: status });
      } else if (Array.isArray(status) && status.length === 1) {
        additionalFilters.push({ field: 'status', operator: '==', value: status[0] });
      } else if (!Array.isArray(status)) {
        additionalFilters.push({ field: 'status', operator: '==', value: status });
      }
    }
    
    if (confirmed !== undefined) {
      additionalFilters.push({ field: 'confirmed', operator: '==', value: confirmed });
    }

    // Build date range filters
    const dateRanges: DateRangeFilter[] = [];
    if (dateFrom || dateTo) {
      dateRanges.push({
        field: 'triggeredAt',
        dateFrom,
        dateTo
      });
    }

    const response = await this.executeQuery(userContext, queryOptions, additionalFilters, dateRanges, organizationId);
    
    return {
      events: response.items,
      totalCount: response.totalCount,
      totalPages: response.totalPages,
      currentPage: response.currentPage,
      pageSize: response.pageSize
    };
  }

  async countWithUserContext(userContext?: UserContext): Promise<number> {
    return this.countWithFilters(userContext);
  }


  private validateEventStatus(status: any): EventStatus {
    if (status === 'triggered' || status === 'pending' || status === 'resolved' || status === 'canceled') {
      return status;
    }
    return 'triggered'; // Default fallback
  }

  private convertSensorData(data: any): SensorData {
    if (!data || typeof data !== 'object') {
      return {};
    }
    
    // Ensure all values are primitive types
    const sensorData: SensorData = {};
    for (const [key, value] of Object.entries(data)) {
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        sensorData[key] = value;
      }
    }
    
    return sensorData;
  }



  async countEventsByLocation(locationId: string, options: Omit<EventCountOptions, 'locationId'>, userContext?: UserContext, organizationId?: string): Promise<number> {
    const {
      sensorId,
      status,
      alertId,
      excludeAlertIds
    } = options;

    const additionalFilters: FilterCondition[] = [];
    
    // Always filter by locationId for this method
    additionalFilters.push({ field: 'locationId', operator: '==', value: locationId });
    
    if (sensorId) {
      additionalFilters.push({ field: 'sensorId', operator: '==', value: sensorId });
    }
    
    if (status) {
      const statuses = Array.isArray(status) ? status : [status];
      if (statuses.length === 1) {
        additionalFilters.push({ field: 'status', operator: '==', value: statuses[0] });
      } else {
        additionalFilters.push({ field: 'status', operator: 'in', value: statuses });
      }
    }

    if (alertId) {
      additionalFilters.push({ field: 'alertId', operator: '==', value: alertId });
    }

    if (excludeAlertIds && excludeAlertIds.length > 0) {
      if (isLocationScopedContext(userContext)) {
        const [total, ...excluded] = await Promise.all([
          this.countWithFilters(userContext, additionalFilters, [], organizationId),
          ...excludeAlertIds.map(id =>
            this.countWithFilters(userContext, [...additionalFilters, { field: 'alertId', operator: '==', value: id }], [], organizationId)
          )
        ]);
        return total - excluded.reduce((sum, c) => sum + c, 0);
      }
      if (excludeAlertIds.length === 1) {
        additionalFilters.push({ field: 'alertId', operator: '!=', value: excludeAlertIds[0] });
      } else {
        additionalFilters.push({ field: 'alertId', operator: 'not-in', value: excludeAlertIds });
      }
    }

    return this.countWithFilters(userContext, additionalFilters, [], organizationId);
  }

  async countByOrganizationId(organizationId: string, options: EventCountOptions, userContext?: UserContext): Promise<number> {
    const {
      locationId,
      sensorId,
      status,
      alertId,
      excludeAlertIds
    } = options;

    const additionalFilters: FilterCondition[] = [];
    
    if (locationId) {
      additionalFilters.push({ field: 'locationId', operator: '==', value: locationId });
    }
    
    if (sensorId) {
      additionalFilters.push({ field: 'sensorId', operator: '==', value: sensorId });
    }
    
    if (status) {
      const statuses = Array.isArray(status) ? status : [status];
      if (statuses.length === 1) {
        additionalFilters.push({ field: 'status', operator: '==', value: statuses[0] });
      } else {
        additionalFilters.push({ field: 'status', operator: 'in', value: statuses });
      }
    }

    if (alertId) {
      additionalFilters.push({ field: 'alertId', operator: '==', value: alertId });
    }

    if (excludeAlertIds && excludeAlertIds.length > 0) {
      if (isLocationScopedContext(userContext)) {
        const [total, ...excluded] = await Promise.all([
          this.countWithFilters(userContext, additionalFilters, [], organizationId),
          ...excludeAlertIds.map(id =>
            this.countWithFilters(userContext, [...additionalFilters, { field: 'alertId', operator: '==', value: id }], [], organizationId)
          )
        ]);
        return total - excluded.reduce((sum, c) => sum + c, 0);
      }
      if (excludeAlertIds.length === 1) {
        additionalFilters.push({ field: 'alertId', operator: '!=', value: excludeAlertIds[0] });
      } else {
        additionalFilters.push({ field: 'alertId', operator: 'not-in', value: excludeAlertIds });
      }
    }

    return this.countWithFilters(userContext, additionalFilters, [], organizationId);
  }
}